import type { ReactElement } from 'react'
import { useCopyToClipboard } from './use-copy-to-clipboard.ts'

/**
 * Monospace for machine things: ids, node paths, argv, hashes, key names,
 * session ids.
 *
 * The brief calls this the single change that does most for "looks like a
 * real tool", and the reason is legibility rather than taste — a session
 * id in a proportional font cannot be compared against another one by eye,
 * and `/instance/dromos-inc/platforms/stoaria` loses its structure.
 *
 * `children` is `string`, not `ReactNode`: the copy button has to have
 * something exact to put on the clipboard, and "copy the rendered
 * subtree" is a different, worse component.
 */

export interface CodeProps {
  children: string
  /** Block form: its own `bg-sunken` well, wraps, scrolls. Inline is the
   * default because most machine strings in this app sit in a sentence or
   * a table cell. */
  isBlock?: boolean
  isCopyable?: boolean
  /** What the copy button is copying, for its accessible name — "Copy
   * session id" reads very differently from nine identical "Copy"s. */
  copyLabel?: string
}

export function Code({
  children,
  isBlock = false,
  isCopyable = false,
  copyLabel = 'Copy',
}: CodeProps): ReactElement {
  const { message, copy } = useCopyToClipboard(children)

  const code = (
    <code
      className={
        isBlock
          ? 'block overflow-x-auto font-mono text-xs whitespace-pre-wrap'
          : 'rounded-sm bg-sunken px-1 py-0.5 font-mono text-xs'
      }
    >
      {children}
    </code>
  )

  // The bare inline case is by far the most common — an id in a sentence,
  // an argv pattern in a cell — and it has no wrapper at all so it can sit
  // inside running text without upsetting the line box.
  if (!isCopyable && !isBlock) return code

  return (
    <div
      className={
        isBlock
          ? 'relative rounded-md border border-subtle bg-sunken p-2'
          : 'inline-flex items-center gap-1'
      }
    >
      {code}
      {isCopyable && (
        <button
          type="button"
          onClick={copy}
          aria-label={copyLabel}
          className={`shrink-0 rounded-sm border border-transparent px-1 text-xs text-faint transition-colors hover:bg-elevated hover:text-fg ${isBlock ? 'absolute top-1.5 right-1.5' : ''}`}
        >
          {/* A glyph plus the aria-label, not a glyph alone: the button is
              two characters wide and the label is what names it. */}
          <span aria-hidden="true">⧉</span>
        </button>
      )}
      {/* Always mounted, even with nothing to say. A `role="status"` node
          that appears at the same moment as its text is frequently missed
          by screen readers — the region has to pre-exist the update. */}
      <span role="status" aria-live="polite" className="sr-only">
        {message}
      </span>
    </div>
  )
}
